import type { RevitFamily } from '../../api/revit/types';
import {
  familyHasCompatibleParameter,
  getFamilyParameterBindingKind,
  selectFamilyParameterMutationTargets,
} from '../../lib/revit/familyParameterTargetSelection';
import { sanitizeRevitParameterNames } from '../../lib/revitParameterNames';
import type { AvailableProjectParameterDescriptor } from '../../lib/revit/projectParameterMutationPlan';
import { resolveBoltParameterTypeHints } from './boltParameterTypes';
import type {
  BoltProjectParameterCategory,
  ConnectorParameterBindingKind,
  ConnectorParameterDestination,
} from '../../types';
import {
  BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  DEFAULT_BOLT_PROJECT_PARAMETER_CATEGORIES,
} from '../../types';

export type BoltModelingType = 'BOLT' | 'GASKET';

export const BOLT_REFERENCE_FAMILY_FILES: Record<BoltModelingType, string> = {
  BOLT: 'CS150_PiX_Fastern_StudBoltSet.rfa',
  GASKET: 'DUMMY_PiX_Faternes_Gasket+BoltProp.rfa',
};

export const DEFAULT_BOLT_PARAMETER_NAMES = sanitizeRevitParameterNames([
  'PiX_PPG_920_BoltSet_ON',
  'PiX_PPG_914_BoltSet_Description',
  'PiX_PPG_912_BoltSet_Diameter',
  'PiX_PPG_910_BoltSet_Quantity',
  'PiX_PPG_911_BoltSet_BoltLength',
  'PiX_PPG_916_BoltSet_Material',
  'PiX_PPG_915_BoltSet_Standard',
]);

export interface BoltConfigurationInput {
  objectTypeParameterName: string;
  objectTypeBindingKind: ConnectorParameterBindingKind;
  parameterDestination?: ConnectorParameterDestination;
  parameterBindingKind?: ConnectorParameterBindingKind;
  projectParameterCategories?: string[];
  boltModelingChoice: string;
  boltSetActiveValue: string;
  boltItemDescriptionValue: string;
  boltDiaValue: string;
  boltQuantityValue: string;
  boltLengthValue: string;
  boltMaterialValue: string;
  boltStandardValue: string;
}

export interface MatchingFamily {
  id: string;
  name: string;
  category: string;
}

export type BoltProjectParameterDescriptor = Pick<
  AvailableProjectParameterDescriptor, 'name' | 'parameterType' | 'dataType' | 'isProjectBinding'
>;

export interface BoltFamilyParameterUndoEntry {
  familyId: string;
  familyName: string;
  parameterName: string;
  existedBefore: boolean;
  previousBindingKind?: ConnectorParameterBindingKind;
}

export interface BoltFamilyParameterBindingChange {
  parameterName: string;
  previousBindingKind: ConnectorParameterBindingKind;
  bindingKind: ConnectorParameterBindingKind;
}

export interface BoltDefaultParameterRemoval {
  parameterName: string;
  bindingKind: ConnectorParameterBindingKind;
}

export interface LoadedBoltFamilyReplacementPlan {
  loadedFamily: RevitFamily | null;
  loadedFamilyMatchesObjectType: boolean;
  selectedParameterNames: string[];
  selectedNamesToAdd: string[];
  selectedParametersToRebind: BoltFamilyParameterBindingChange[];
  defaultParametersToRemove: BoltDefaultParameterRemoval[];
  verificationParameterNames: string[];
  isVerified: boolean;
}

export type BoltSetupPlan =
  | {
      action: 'no_action';
      reason: 'no_parameters';
      targetObjectType: BoltModelingType;
      parameterNames: string[];
    }
  | {
      action: 'no_action';
      reason: 'family_parameters_available';
      targetObjectType: BoltModelingType;
      parameterNames: string[];
      bindingKind: ConnectorParameterBindingKind;
      matchingFamilies: MatchingFamily[];
      targetFamiliesNeedingMutation: MatchingFamily[];
    }
  | {
      action: 'add_project_parameters';
      targetObjectType: BoltModelingType;
      parameterNames: string[];
      bindingKind: ConnectorParameterBindingKind;
      projectParameterCategories: BoltProjectParameterCategory[];
      matchingFamilies: MatchingFamily[];
    }
  | {
      action: 'add_family_parameters';
      targetObjectType: BoltModelingType;
      parameterNames: string[];
      bindingKind: ConnectorParameterBindingKind;
      matchingFamilies: MatchingFamily[];
      targetFamiliesNeedingMutation: MatchingFamily[];
    }
  | {
      action: 'load_reference_family';
      targetObjectType: BoltModelingType;
      parameterNames: string[];
      bindingKind: ConnectorParameterBindingKind;
      projectParameterCategories?: BoltProjectParameterCategory[];
    };

function getParameterDestination(configInput: BoltConfigurationInput): ConnectorParameterDestination {
  return configInput.parameterDestination === 'project' ? 'project' : 'family';
}

function getParameterBindingKind(configInput: BoltConfigurationInput): ConnectorParameterBindingKind {
  return configInput.parameterBindingKind === 'instance' ? 'instance' : 'type';
}

export function normalizeBoltProjectParameterCategories(
  categories: string[] | undefined
): BoltProjectParameterCategory[] {
  const allowedCategories = new Set<string>(BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS);
  const normalizedCategories = Array.from(
    new Set(
      (categories ?? []).filter(
        (category): category is BoltProjectParameterCategory =>
          typeof category === 'string' && allowedCategories.has(category)
      )
    )
  );

  return normalizedCategories.length > 0
    ? normalizedCategories
    : [...DEFAULT_BOLT_PROJECT_PARAMETER_CATEGORIES];
}

export function getTargetObjectType(boltModelingChoice: string): BoltModelingType {
  return boltModelingChoice === 'yes' ? 'BOLT' : 'GASKET';
}

export function getBoltBaseFamilyValue(boltModelingChoice: string): 'BoltBased' | 'GasketBased' {
  return boltModelingChoice === 'yes' ? 'BoltBased' : 'GasketBased';
}

export function extractBoltParameterNames(config: BoltConfigurationInput): string[] {
  const paramNames: string[] = [];

  if (config.boltSetActiveValue) paramNames.push(config.boltSetActiveValue);
  if (config.boltItemDescriptionValue) paramNames.push(config.boltItemDescriptionValue);
  if (config.boltDiaValue) paramNames.push(config.boltDiaValue);
  if (config.boltQuantityValue) paramNames.push(config.boltQuantityValue);
  if (config.boltLengthValue) paramNames.push(config.boltLengthValue);
  if (config.boltMaterialValue) paramNames.push(config.boltMaterialValue);
  if (config.boltStandardValue) paramNames.push(config.boltStandardValue);

  return Array.from(new Set(paramNames.filter(Boolean)));
}

export function findFamiliesWithObjectType(
  families: RevitFamily[],
  targetObjectType: BoltModelingType,
  objectTypeParameterName: string
): MatchingFamily[] {
  const discriminatorKey = objectTypeParameterName.trim().toLowerCase();
  if (!discriminatorKey) {
    return [];
  }
  const matchingFamilies: MatchingFamily[] = [];

  for (const family of families) {
    for (const familyType of family.types || []) {
      const objectTypeParam = familyType.parameterValues?.find(
        (pv) => pv.parameterName.trim().toLowerCase() === discriminatorKey
      );

      if (objectTypeParam && String(objectTypeParam.value).toUpperCase() === targetObjectType) {
        matchingFamilies.push({
          id: family.id,
          name: family.name,
          category: family.category,
        });
        break;
      }
    }
  }

  return matchingFamilies;
}

function familyHasParameter(
  family: RevitFamily,
  parameterName: string,
  bindingKind?: ConnectorParameterBindingKind
): boolean {
  return familyHasCompatibleParameter(family, parameterName, bindingKind);
}

export function getBoltFamilyVerificationParameterNames(
  parameterNames: string[],
  objectTypeParameterName: string
): string[] {
  return sanitizeRevitParameterNames([
    ...DEFAULT_BOLT_PARAMETER_NAMES,
    ...parameterNames,
    objectTypeParameterName,
  ]);
}

function findLoadedFamily(
  families: RevitFamily[],
  targetObjectType: BoltModelingType,
  objectTypeParameterName: string,
  loadedFamilyId?: string,
  loadedFamilyName?: string
): RevitFamily | null {
  const normalizedLoadedFamilyId = loadedFamilyId?.trim().toLowerCase();
  if (normalizedLoadedFamilyId) {
    const familyById = families.find(
      (family) => family.id?.trim().toLowerCase() === normalizedLoadedFamilyId
    );
    if (familyById) {
      return familyById;
    }
  }

  const normalizedLoadedFamilyName = loadedFamilyName?.trim().toLowerCase();
  if (normalizedLoadedFamilyName) {
    const matchingByName = families.filter(
      (family) => family.name?.trim().toLowerCase() === normalizedLoadedFamilyName
    );
    if (matchingByName.length === 1) {
      // Editing and reloading a family replaces its Revit ElementId. Its name
      // remains stable and is unique in a project, while OBJECT-TYPE may only
      // be visible after the follow-up family-scoped snapshot is merged.
      return matchingByName[0];
    }

    const matchingByNameAndObjectType = matchingByName.find(
      (family) => findFamiliesWithObjectType(
        [family],
        targetObjectType,
        objectTypeParameterName
      ).length > 0
    );
    if (matchingByNameAndObjectType) {
      return matchingByNameAndObjectType;
    }
  }

  const matchingFamilyIds = new Set(
    findFamiliesWithObjectType(
      families,
      targetObjectType,
      objectTypeParameterName
    ).map((family) => family.id)
  );

  return families.find((family) => matchingFamilyIds.has(family.id)) ?? null;
}

export function createLoadedBoltFamilyReplacementPlan(input: {
  families: RevitFamily[];
  loadedFamilyId?: string;
  loadedFamilyName?: string;
  targetObjectType: BoltModelingType;
  objectTypeParameterName: string;
  bindingKind: ConnectorParameterBindingKind;
  selectedParameterNames: string[];
}): LoadedBoltFamilyReplacementPlan {
  const selectedParameterNames = sanitizeRevitParameterNames(input.selectedParameterNames);
  const loadedFamily = findLoadedFamily(
    input.families,
    input.targetObjectType,
    input.objectTypeParameterName,
    input.loadedFamilyId,
    input.loadedFamilyName
  );
  const verificationParameterNames = getBoltFamilyVerificationParameterNames(
    selectedParameterNames,
    input.objectTypeParameterName
  );

  if (!loadedFamily) {
    return {
      loadedFamily: null,
      loadedFamilyMatchesObjectType: false,
      selectedParameterNames,
      selectedNamesToAdd: selectedParameterNames,
      selectedParametersToRebind: [],
      defaultParametersToRemove: [],
      verificationParameterNames,
      isVerified: false,
    };
  }

  const selectedNameLookup = new Set(
    selectedParameterNames.map((parameterName) => parameterName.trim().toLowerCase())
  );
  const loadedFamilyMatchesObjectType =
    findFamiliesWithObjectType(
      [loadedFamily],
      input.targetObjectType,
      input.objectTypeParameterName
    ).length > 0;

  const selectedNamesToAdd: string[] = [];
  const selectedParametersToRebind: BoltFamilyParameterBindingChange[] = [];
  for (const parameterName of selectedParameterNames) {
    const actualBindingKind = getFamilyParameterBindingKind(loadedFamily, parameterName);
    if (!actualBindingKind) {
      selectedNamesToAdd.push(parameterName);
      continue;
    }

    if (actualBindingKind !== input.bindingKind) {
      selectedParametersToRebind.push({
        parameterName,
        previousBindingKind: actualBindingKind,
        bindingKind: input.bindingKind,
      });
    }
  }

  const defaultParametersToRemove = DEFAULT_BOLT_PARAMETER_NAMES.flatMap(
    (parameterName): BoltDefaultParameterRemoval[] => {
      if (selectedNameLookup.has(parameterName.trim().toLowerCase())) {
        return [];
      }

      const actualBindingKind = getFamilyParameterBindingKind(loadedFamily, parameterName);
      return actualBindingKind ? [{ parameterName, bindingKind: actualBindingKind }] : [];
    }
  );

  return {
    loadedFamily,
    loadedFamilyMatchesObjectType,
    selectedParameterNames,
    selectedNamesToAdd,
    selectedParametersToRebind,
    defaultParametersToRemove,
    verificationParameterNames,
    isVerified:
      loadedFamilyMatchesObjectType &&
      selectedNamesToAdd.length === 0 &&
      selectedParametersToRebind.length === 0 &&
      defaultParametersToRemove.length === 0,
  };
}

export function buildBoltFamilyUndoEntries(
  families: RevitFamily[],
  targetObjectType: BoltModelingType,
  objectTypeParameterName: string,
  parameterNames: string[]
): BoltFamilyParameterUndoEntry[] {
  const matchingFamilyIds = new Set(
    findFamiliesWithObjectType(
      families,
      targetObjectType,
      objectTypeParameterName
    ).map((family) => family.id)
  );

  return families
    .filter((family) => matchingFamilyIds.has(family.id) && family.id && family.name)
    .flatMap((family) =>
      sanitizeRevitParameterNames(parameterNames).map((parameterName) => {
        const previousBindingKind = familyHasParameter(family, parameterName, 'instance')
          ? 'instance'
          : familyHasParameter(family, parameterName, 'type')
            ? 'type'
            : null;

        return {
          familyId: family.id,
          familyName: family.name,
          parameterName,
          existedBefore: previousBindingKind !== null,
          ...(previousBindingKind ? { previousBindingKind } : {}),
        };
      })
    );
}

export function createBoltSetupPlan(
  configInput: BoltConfigurationInput,
  families: RevitFamily[]
): BoltSetupPlan {
  const parameterNames = extractBoltParameterNames(configInput);
  const targetObjectType = getTargetObjectType(configInput.boltModelingChoice);
  if (parameterNames.length === 0) {
    return {
      action: 'no_action',
      reason: 'no_parameters',
      targetObjectType,
      parameterNames,
    };
  }

  const bindingKind = getParameterBindingKind(configInput);
  const matchingFamilies = findFamiliesWithObjectType(
    families,
    targetObjectType,
    configInput.objectTypeParameterName
  );

  if (getParameterDestination(configInput) === 'project') {
    const projectParameterCategories = normalizeBoltProjectParameterCategories(
      configInput.projectParameterCategories
    );

    if (matchingFamilies.length === 0) {
      return {
        action: 'load_reference_family',
        targetObjectType,
        parameterNames,
        bindingKind,
        projectParameterCategories,
      };
    }

    return {
      action: 'add_project_parameters',
      targetObjectType,
      parameterNames,
      bindingKind,
      projectParameterCategories,
      matchingFamilies,
    };
  }

  if (matchingFamilies.length > 0) {
    const matchingFamilyIds = new Set(matchingFamilies.map((family) => family.id));
    const targetSelection = selectFamilyParameterMutationTargets(
      families,
      parameterNames,
      bindingKind,
      (family) => matchingFamilyIds.has(family.id),
      resolveBoltParameterTypeHints(configInput)
    );

    if (targetSelection.targetFamilyIds.length === 0) {
      return {
        action: 'no_action',
        reason: 'family_parameters_available',
        targetObjectType,
        parameterNames,
        bindingKind,
        matchingFamilies,
        targetFamiliesNeedingMutation: [],
      };
    }

    return {
      action: 'add_family_parameters',
      targetObjectType,
      parameterNames,
      bindingKind,
      matchingFamilies,
      targetFamiliesNeedingMutation: targetSelection.targetFamilies.map((family) => ({
        id: family.id,
        name: family.name,
        category: family.category,
      })),
    };
  }

  return {
    action: 'load_reference_family',
    targetObjectType,
    parameterNames,
    bindingKind,
  };
}
